/**
 * useKioskTokenRefresh Hook
 * 
 * Automatically refreshes the kiosk JWT before it expires.
 * This ensures the kiosk stays authenticated indefinitely
 * without requiring manual re-pairing.
 * 
 * @module kiosk/hooks/useKioskTokenRefresh
 */

import { useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import {
    getKioskToken,
    decodeJwtPayload,
    KIOSK_INTERVALS
} from '../utils/kioskUtils';

// Refresh when token has less than this time remaining (24 hours)
const REFRESH_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// Check every hour if refresh is needed
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Wrap Sentry calls in try/catch so a Sentry init issue never causes a
 * refresh failure cascade — this hook runs unattended on kiosks in schools
 * and we never want it to throw at the React boundary.
 */
function breadcrumb(message: string, data?: Record<string, unknown>, level: Sentry.SeverityLevel = 'info') {
    try {
        Sentry.addBreadcrumb({ category: 'kiosk.refresh', message, level, data });
    } catch {
        // Sentry not initialised — fine.
    }
}

function captureRefreshFailure(message: string, data?: Record<string, unknown>) {
    breadcrumb(message, data, 'error');
    try {
        // Capture as a low-volume warning event so we can alert/triage when
        // kiosks start dying again. Hourly cadence + only-on-failure means
        // even a catastrophic outage caps at ~24 events/kiosk/day.
        Sentry.captureMessage(`kiosk-refresh: ${message}`, {
            level: 'warning',
            tags: { component: 'kiosk-refresh' },
            extra: data,
        });
    } catch {
        // Sentry not initialised — fine.
    }
}

/**
 * Attempts to refresh the kiosk JWT token.
 * Returns true if refresh was successful, false otherwise.
 *
 * Side effects on success:
 *   - localStorage.fw_kiosk_jwt updated (used by every kiosk supabase call)
 *   - native DPAPI storage updated via window.kiosk.setToken (so a cold
 *     Electron restart picks up the refreshed token, not the stale one
 *     migrated at original pair time)
 */
async function refreshKioskToken(): Promise<boolean> {
    const currentToken = getKioskToken();
    if (!currentToken) {
        breadcrumb('skip: no token in localStorage');
        return false;
    }

    try {
        // Get the Supabase URL from environment
        const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL ||
            (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_URL : undefined);

        if (!supabaseUrl) {
            captureRefreshFailure('supabase url not configured');
            return false;
        }

        // Call the refresh edge function
        const response = await fetch(`${supabaseUrl}/functions/v1/kiosk-refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${currentToken}`
            }
        });

        if (!response.ok) {
            const errorData: { code?: string; message?: string } =
                await response.json().catch(() => ({}));
            captureRefreshFailure('http error', {
                status: response.status,
                code: errorData.code,
                message: errorData.message,
            });
            // 401/403 from a revoked or invalid token → kick the device
            // back to setup so it doesn't keep firing failed refreshes
            // forever on a dead pairing. 5xx and network errors leave the
            // current (possibly still-valid) token in place.
            if (response.status === 401 || response.status === 403) {
                breadcrumb('hard-fail: clearing local token and redirecting to /kiosk/setup', {
                    status: response.status,
                    code: errorData.code,
                });
                try { window.localStorage.removeItem('fw_kiosk_jwt'); } catch { /* ignore */ }
                try { void window.kiosk?.clearToken?.(); } catch { /* ignore */ }
                try { window.location.replace('/kiosk/setup'); } catch { /* ignore */ }
            }
            return false;
        }

        const data = await response.json();

        if (data.status !== 'ok' || !data.access_token) {
            captureRefreshFailure('malformed ok response', { data });
            return false;
        }

        // Persist to BOTH stores. localStorage powers every in-page Supabase
        // call; native storage is what Electron's main process reads on cold
        // start (token.dat under %ProgramData% on Windows). Without the
        // native write, a Windows reboot during the refreshed week would
        // resurrect a stale token and the kiosk would die at the OLD expiry.
        window.localStorage.setItem('fw_kiosk_jwt', data.access_token);
        try {
            await window.kiosk?.setToken?.({ access_token: data.access_token });
        } catch (nativeErr) {
            // Non-fatal — localStorage write succeeded, kiosk keeps working
            // for the current session; only impact is a stale cold-start.
            breadcrumb('native setToken failed (non-fatal)', {
                error: (nativeErr as { message?: string })?.message,
            }, 'warning');
        }
        breadcrumb('token refreshed', { kiosk_id: data.kiosk_id });
        return true;
    } catch (error) {
        captureRefreshFailure('fetch threw', {
            error: (error as { message?: string })?.message,
        });
        return false;
    }
}

/**
 * Hook that monitors JWT expiration and automatically refreshes
 * the token before it expires.
 * 
 * @param enabled - Whether to enable auto-refresh (default: true)
 */
export function useKioskTokenRefresh(enabled: boolean = true): void {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const isRefreshingRef = useRef(false);

    const checkAndRefresh = useCallback(async () => {
        if (!enabled || isRefreshingRef.current) return;

        const token = getKioskToken();
        if (!token) return;

        const payload = decodeJwtPayload(token);
        if (!payload?.exp) return;

        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;

        // If token expires within threshold, refresh it
        if (timeUntilExpiry > 0 && timeUntilExpiry <= REFRESH_THRESHOLD_MS) {
            console.log(`[Kiosk Token Refresh] Token expires in ${Math.round(timeUntilExpiry / 3600000)}h, refreshing...`);

            isRefreshingRef.current = true;
            try {
                const success = await refreshKioskToken();
                if (!success) {
                    console.warn('[Kiosk Token Refresh] Refresh failed, will retry later');
                }
            } finally {
                isRefreshingRef.current = false;
            }
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;

        // Check immediately on mount
        checkAndRefresh();

        // Set up periodic checking
        intervalRef.current = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, checkAndRefresh]);
}
